import { useState, useEffect, useRef } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InboxIcon from "@mui/icons-material/Inbox";

import { UrlApi } from "../../../../services/apirest";
import { DownloadFiles } from "../../../../services/DownloadFiles";

const POLL_INTERVAL_MS = 10_000; // 10 segundos

const GrandesEmpresas = (props) => {
  const [openclose, setOpenClose] = useState(props.openclose);
  const [fileList, setFileList] = useState([]);
  const [archivos, setArchivos] = useState([]);
  const [imageUrl, setImageUrl] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Estado del job activo
  const [jobId, setJobId] = useState(null);
  const [jobMessage, setJobMessage] = useState("");
  const intervalRef = useRef(null);
  const prevMessageRef = useRef(""); // evita mostrar el mismo toast dos veces seguidas

  const { showToast } = props;

  useEffect(() => {
    if (imageUrl) window.open(imageUrl, "_blank", "noreferrer");
  }, [imageUrl]);

  const fetchArchivos = () => {
    if (!props.fileId) return;
    fetch(UrlApi + "/uploadfiles/todownload/" + props.fileId)
      .then((data) => data.json())
      .then((data) => setArchivos(data))
      .catch(console.error);
  };

  useEffect(() => {
    fetchArchivos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.fileId]);

  // ── Polling: se activa cuando hay un jobId ─────────────────────────────
  useEffect(() => {
    if (!jobId) return;

    const poll = async () => {
      try {
        const res = await fetch(`${UrlApi}/uploadfiles/job-status/${jobId}`);
        if (!res.ok) return;
        const job = await res.json();
        const msg = job.message || "";
        setJobMessage(msg);

        // Mostrar toast solo cuando el mensaje cambia (evita spam del mismo texto)
        if (msg && msg !== prevMessageRef.current) {
          prevMessageRef.current = msg;

          if (job.status === "pending" || job.status === "processing") {
            showToast(msg, "info");
          }
        }

        if (job.status === "done") {
          clearInterval(intervalRef.current);
          showToast(
            msg || "Archivo de Grandes Empresas procesado correctamente",
            "success"
          );
          setJobId(null);
          setJobMessage("");
          setFileList([]);
          setIsSubmitted(false);
          props.isSubmitting?.(false);
          fetchArchivos();
        } else if (job.status === "error") {
          clearInterval(intervalRef.current);
          showToast(msg || "Error al procesar el archivo", "error");
          setJobId(null);
          setJobMessage("");
          setIsSubmitted(false);
          props.isSubmitting?.(false);
        }
      } catch (err) {
        console.error("[GrandesEmpresas] Error polling job:", err);
      }
    };

    poll();
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const borraAdjunto = (e, id, archive) => {
    e.preventDefault();
    if (window.confirm("¿Seguro de borrar el archivo " + archive + "?")) {
      fetch(UrlApi + "/uploadfiles/todownload/" + id, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      }).then((res) => {
        if (res.ok) {
          showToast("Archivo borrado correctamente", "success");
          fetchArchivos();
        } else {
          showToast("No se pudo borrar el archivo", "error");
        }
      });
    }
  };

  const handleFileChange = (e) => setFileList(e.target.files);

  const removefiles = (idx) => {
    const updated = Array.from(fileList).filter((_, i) => i !== idx);
    setFileList(updated);
  };

  const handleClose = () => {
    setOpenClose(false);
    if (props.onClose) props.onClose();
  };

  const handleUploadClick = async () => {
    if (!fileList || fileList.length === 0) {
      showToast("No hay archivos para guardar", "warning");
      return;
    }
    setIsSubmitted(true);
    props.isSubmitting?.(true);

    const data = new FormData();
    data.append("uid", props.fileId);
    data.append("usuario", localStorage.getItem("user"));
    for (let i = 0; i < fileList.length; i++) {
      data.append("archivo", fileList[i]);
    }

    try {
      const res = await fetch(UrlApi + "/uploadfiles/upload-padron-grandes", {
        method: "POST",
        body: data,
      });

      let resData = null;
      const ct = res.headers.get("content-type") || "";
      if (ct.includes("application/json")) resData = await res.json();

      if (res.status === 202) {
        // Archivo aceptado — arrancar polling
        prevMessageRef.current = "";
        setJobId(resData.job_id);
        setJobMessage("Archivo recibido. Procesando en segundo plano...");
        showToast("Archivo recibido. Procesando en segundo plano...", "info");
      } else if (res.status === 409) {
        showToast(
          resData?.message ?? "Ya hay un proceso activo. Esperá a que termine.",
          "warning"
        );
        setIsSubmitted(false);
        props.isSubmitting?.(false);
      } else {
        showToast(resData?.message ?? "Error al subir el archivo", "error");
        setIsSubmitted(false);
        props.isSubmitting?.(false);
      }
    } catch (err) {
      console.error(err);
      showToast("Error al guardar los archivos", "error");
      setIsSubmitted(false);
      props.isSubmitting?.(false);
    }
  };

  const fetchPdf = async (archivo) => {
    const response = await fetch(UrlApi + "/uploadfiles/uploadspdf/" + archivo);
    setImageUrl(URL.createObjectURL(await response.blob()));
  };

  const fetchImage = async (archivo) => {
    const response = await fetch(UrlApi + "/uploadfiles/uploads/" + archivo);
    setImageUrl(URL.createObjectURL(await response.blob()));
  };

  const ViewFiles = (archivo, extension) => {
    if (extension === "application/pdf") fetchPdf(archivo);
    else fetchImage(archivo);
  };

  return (
    <Dialog
      open={openclose}
      // Bloquear cierre mientras procesa para mantener el polling activo
      onClose={isSubmitted ? undefined : handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #1F618D 0%, #2980B9 100%)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          gap: 1,
          py: 2,
        }}
      >
        <CloudUploadIcon />
        <Typography variant="h6" fontWeight={700}>
          {props.tituloDialogo}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* ── Estado del job activo ── */}
        {isSubmitted && jobId ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 5,
              gap: 2,
            }}
          >
            <CircularProgress size={52} />
            <Typography variant="body1" fontWeight={600} textAlign="center">
              Procesando archivo...
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ maxWidth: 340 }}
            >
              {jobMessage || "Esto puede tardar varios minutos."}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Zona de selección */}
            <Box
              sx={{
                border: "2px dashed #90C4E4",
                borderRadius: 2,
                p: 2.5,
                textAlign: "center",
                backgroundColor: "#F4F8FB",
                mb: 2,
              }}
            >
              <Button
                variant="outlined"
                component="label"
                startIcon={<ContentPasteSearchIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 2,
                  borderColor: "#1F618D",
                  color: "#1F618D",
                }}
              >
                Seleccionar archivo .csv
                <input
                  hidden
                  type="file"
                  multiple
                  accept=".csv"
                  onChange={handleFileChange}
                />
              </Button>
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
                mt={1}
              >
                Solo archivos .csv
              </Typography>
            </Box>

            {/* Archivos seleccionados */}
            {fileList && Array.from(fileList).length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  mb={0.5}
                  display="block"
                >
                  ARCHIVOS A SUBIR
                </Typography>
                <Table size="small">
                  <TableBody>
                    {Array.from(fileList).map((file, i) => (
                      <TableRow
                        key={i}
                        sx={{ "&:last-child td": { border: 0 } }}
                      >
                        <TableCell padding="none" sx={{ width: 40 }}>
                          <IconButton
                            size="small"
                            onClick={() => removefiles(i)}
                          >
                            <DeleteIcon fontSize="small" color="error" />
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                          >
                            <AttachFileIcon fontSize="small" color="action" />
                            <Typography variant="body2">{file.name}</Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              ({(file.size / 1000).toFixed(2)} KB)
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Archivos ya subidos */}
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              mb={0.5}
              display="block"
            >
              ARCHIVOS CARGADOS
            </Typography>
            {archivos && archivos.length > 0 ? (
              <Table size="small">
                <TableBody>
                  {archivos.map((archivo) => (
                    <TableRow
                      key={archivo.id}
                      sx={{ "&:last-child td": { border: 0 } }}
                    >
                      <TableCell padding="none">
                        <IconButton
                          size="small"
                          color="secondary"
                          disabled={
                            archivo.extension !== "application/pdf" &&
                            archivo.extension !== "image/jpeg"
                          }
                          onClick={() =>
                            ViewFiles(
                              archivo.id_archivo + "-" + archivo.nombre,
                              archivo.extension
                            )
                          }
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() =>
                            DownloadFiles(
                              archivo.id_archivo + "-" + archivo.nombre
                            )
                          }
                        >
                          <DownloadForOfflineIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) =>
                            borraAdjunto(e, archivo.id, archivo.nombre)
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{archivo.nombre}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Box
                sx={{ textAlign: "center", py: 3, color: "text.disabled" }}
              >
                <InboxIcon sx={{ fontSize: 36, mb: 0.5, opacity: 0.4 }} />
                <Typography variant="body2">No hay archivos cargados</Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #eee" }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          color="error"
          disabled={isSubmitted}
          sx={{ textTransform: "none", borderRadius: 2 }}
        >
          Volver
        </Button>
        {!isSubmitted && (
          <Button
            disabled={!fileList || fileList.length === 0}
            onClick={handleUploadClick}
            variant="contained"
            color="success"
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              minWidth: "100px",
            }}
          >
            Guardar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GrandesEmpresas;
